/**
 * The seven calls the browser is allowed to make. Every one of them is a path
 * nginx whitelists explicitly (infra/peterhof.aiagentslab.ru.conf); anything
 * outside the list answers 404 by default, which is the safe side of the
 * mistake to be on.
 */

export const API_BASE = '/api';

export type RunContext = {
  user_id: string;
  channel: 'web';
  model?: string;
  /**
   * The SECOND contour's model for this run — `peterhof_tandem`'s background
   * worker, or the helper behind `delegate` on the two orchestrator graphs
   * (2026-09-16). A field of its own because `model` is already the contour the
   * visitor is reading, and named `*_override` because each graph has a
   * `worker_model` of its own (the deploy's claim for the header). Absent means
   * «leave the deploy's helper alone»; present re-points the model AND, on the
   * orchestrators, the gateway it is reached through, resolved from the name.
   * No registered graph reads it since those graphs left the registry
   * (2026-10-15, INV-PH-111).
   */
  worker_model_override?: string;
  temperature?: number;
  /**
   * The FIRST contour's reasoning effort, pinned in the dev menu
   * (`sessionStore.currentReasoningEffort`). Absent = the deploy's own. «Off»
   * is `off` on `peterhof_agent` and `none` everywhere else — the graphs'
   * own spellings.
   */
  reasoning_effort?: 'none' | 'off' | 'low' | 'medium' | 'high';
  /**
   * The SECOND contour's (`sessionStore.currentWorkerReasoningEffort`): the
   * orchestrators' helper, or the tandem's worker, which receives it as its
   * own `reasoning_effort`. Absent = the deploy's own.
   */
  worker_reasoning_effort?: 'none' | 'off' | 'low' | 'medium' | 'high';
  debug?: boolean;
  location_source?: 'gps' | 'manual' | 'map' | 'object' | 'chat';
  location_selection_id?: string;
  location_reset_at?: number;
  /** Captured when the visitor request is built, before any location lookup. */
  location_request_at?: number;
  location_at?: number;
  location_accuracy_m?: number;
  /** The name of the place picked in «Указать адрес» / on the map (never for GPS); shown as the start of a trip. */
  location_label?: string;
  latitude?: number;
  longitude?: number;
  /**
   * `peterhof_live` only: the `delegation_id` of a finished job, sent on the
   * REPORT run `App.tsx`'s `scheduleReport` starts once the chat goes idle.
   * That run carries no user bubble (`input.messages` is `[]`) and no other
   * context field — the graph reads this one to know which job's brief to
   * retell, gates every consent belt shut (no trailing `HumanMessage` means
   * `turn_utterance` is empty, §plan «Архитектура»), and answers with an
   * ordinary AI message under a fresh id.
   */
  live_report?: string;
  /**
   * The board card a visitor's line came from (`a2ui/actions.ts`, `dispatchTap`),
   * for a graph that reads it; dropped by one that does not declare it.
   */
  a2ui_action?: Record<string, unknown>;
  /**
   * `peterhof_agent` only (asst-peterhof-guide#142): the questionnaire the start
   * button that sent this line opens — the graph opens it in code. Never sent to
   * another graph (`sessionStore.readsStarter`).
   */
  starter?: string;
  /**
   * `peterhof_v2` only (INV-PH-166): a saved plan's link was opened — the graph's own token, which its code
   * rebuilds the plan from (`export/returnLink.ts`). Never sent to another graph (`graphFamily.readsTripLink`).
   */
  trip_link?: { token: string };
};

export type StartRunBody = {
  assistant_id: string;
  // `[]` on `peterhof_live`'s report run (`live_report` above) — no visitor
  // utterance started it, so there is nothing to echo as a `HumanMessage`.
  //
  // Absent (rather than `[]`) when `command` carries a resume: reagent reads
  // exactly one of the two, and sending an empty `input` alongside a resume
  // would be indistinguishable from "resume with no new message" only by luck.
  input?: { messages: { role: 'user'; content: string }[] };
  /**
   * Answers a pending `interrupt()` on this thread (`peterhof_agent`'s
   * questionnaire tool) instead of starting a fresh turn — the SAME endpoint,
   * with `command` in place of `input`. `resume`'s shape is the interrupt's
   * own contract (`store/questionnaireStore.ts`'s `ResumeValue`): the graph
   * reads whatever the paused `interrupt()` call is waiting for, and this
   * transport does not need to know its shape beyond "some JSON value".
   */
  command?: { resume: unknown };
  stream_mode: string[];
  on_disconnect: 'continue' | 'cancel';
  context: RunContext;
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly retryAfter?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = `http_${response.status}`;
  let message = response.statusText || 'request failed';
  try {
    const body = await response.json();
    if (body && typeof body === 'object') {
      if (typeof body.error === 'string') code = body.error;
      if (typeof body.message === 'string') message = body.message;
      if (typeof body.detail === 'string') message = body.detail;
    }
  } catch {
    /* an error body that is not JSON tells us nothing more than the status */
  }
  const retryHeader = response.headers.get('Retry-After');
  const retryAfter = retryHeader ? Number.parseInt(retryHeader, 10) : undefined;
  return new ApiError(
    response.status,
    code,
    message,
    Number.isFinite(retryAfter) ? retryAfter : undefined,
  );
}

/**
 * Creates the thread under an id we chose ourselves.
 *
 * Two vanished worries, both checked live: reagent creates a thread implicitly
 * on the first run, so this is not a precondition; and a repeat with
 * `if_exists: "do_nothing"` answers 200, so two tabs racing is not a conflict.
 * We still call it, because a thread with a record of its own is what
 * `GET /threads/{id}/state` reads back after a reload.
 */
export async function createThread(threadId: string, signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${API_BASE}/threads`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ thread_id: threadId, if_exists: 'do_nothing' }),
    signal,
  });
  if (!response.ok) throw await toApiError(response);
}

/** Opens the run's SSE stream. The caller reads the body with `readSse`. */
export async function startRun(
  threadId: string,
  body: StartRunBody,
  signal?: AbortSignal,
): Promise<Response> {
  const response = await fetch(`${API_BASE}/threads/${threadId}/runs/stream`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) throw await toApiError(response);
  return response;
}

/**
 * Re-subscribes to a run that is already going, replaying whatever was missed.
 *
 * `Last-Event-ID` takes the full frame id (`{run_id}_event_{seq}`); `-1` means
 * «from the beginning». Both verified live — see `sse.ts`.
 */
export async function resumeRun(
  threadId: string,
  runId: string,
  lastEventId: string | null,
  signal?: AbortSignal,
): Promise<Response> {
  const response = await fetch(`${API_BASE}/threads/${threadId}/runs/${runId}/stream`, {
    method: 'GET',
    headers: { accept: 'text/event-stream', 'Last-Event-ID': lastEventId ?? '-1' },
    signal,
  });
  if (!response.ok) throw await toApiError(response);
  return response;
}

/**
 * Server-side cancel behind the «Стоп» button.
 *
 * `POST …/cancel` answers 200 (checked live); the `PATCH …/runs/{rid}` path the
 * other reading proposed answers 422 — it is the run-update route and wants a
 * whole run body, not a status. So there is one path here and one nginx
 * location, and the fallback branch this file used to carry is gone.
 */
export async function cancelRun(
  threadId: string,
  runId: string,
  action: 'cancel' | 'interrupt' = 'interrupt',
): Promise<void> {
  const response = await fetch(
    `${API_BASE}/threads/${threadId}/runs/${runId}/cancel?action=${action}`,
    { method: 'POST', signal: withDeadline(undefined, 5000) },
  );
  if (!response.ok && response.status !== 404 && response.status !== 409) {
    throw await toApiError(response);
  }
}

export type RunStatus = {
  status: string;
  run_id: string;
};

/**
 * A deadline for every request that is NOT the stream itself.
 *
 * **This exists because of a live hang, and it is worth naming.** `getRun` is
 * the last rung of the recovery ladder, and it had no timeout and no signal.
 * When the path to the guide went away mid-turn, the dev proxy held the socket
 * open instead of failing: the `fetch` never settled, so the turn's promise
 * never settled either, and the visitor was left with half an answer under a
 * spinner that could not stop — no error, no «Повторить», reload the only way
 * out. A ladder whose last rung can hang for ever is worse than no ladder.
 */
function withDeadline(signal: AbortSignal | undefined, ms: number): AbortSignal {
  const deadline = AbortSignal.timeout(ms);
  return signal ? AbortSignal.any([signal, deadline]) : deadline;
}

/**
 * `getRun`'s deadline. 5 s read a finished answer as lost on a slow 3G link: the run record is ~30 KB and the
 * plan's photographs were loading beside it, so all three asks timed out and «Гид сейчас не отвечает» stood
 * under a complete plan (first-user QA, dev stand behind a «3G» proxy, 21.10.2026). Still a deadline — the
 * ladder may not hang — but one a slow phone can meet.
 */
export const RUN_STATUS_DEADLINE_MS = 12_000;

/**
 * The last rung of the recovery ladder: a container restart is a SILENT TCP
 * close — no `error`, no `end` — so «is it still going?» has to be asked.
 */
export async function getRun(
  threadId: string,
  runId: string,
  signal?: AbortSignal,
): Promise<RunStatus> {
  const response = await fetch(`${API_BASE}/threads/${threadId}/runs/${runId}`, {
    signal: withDeadline(signal, RUN_STATUS_DEADLINE_MS),
  });
  if (!response.ok) throw await toApiError(response);
  const body = await response.json();
  return { status: String(body?.status ?? 'unknown'), run_id: String(body?.run_id ?? runId) };
}

/** One entry of `GET /threads/{id}/state`'s `interrupts` array. */
export type ThreadInterrupt = { id: string; value: unknown };

export type ThreadState = {
  values: Record<string, unknown>;
  /**
   * A run paused mid-turn on an `interrupt()` — `peterhof_agent`'s
   * questionnaire tool, today. Empty on every thread that is not mid-
   * interrupt, which is nearly all of them, so a caller that never reads this
   * loses nothing. `interrupts[0].value` carries the SAME contract as the
   * live `values.__interrupt__` frame (`transport/events.ts`'s `interrupt`
   * event) — this is the F5 door onto it.
   */
  interrupts: ThreadInterrupt[];
};

/**
 * Full state from the checkpoint — messages, attachments and (once the canvas
 * graph ships) the board. Outlives the event log, which is kept one hour and is
 * addressed by `run_id`s a reloaded page does not have.
 */
export async function getThreadState(threadId: string, signal?: AbortSignal): Promise<ThreadState> {
  const response = await fetch(`${API_BASE}/threads/${threadId}/state`, {
    signal: withDeadline(signal, 10000),
  });
  if (!response.ok) throw await toApiError(response);
  const body = await response.json();
  const values = body && typeof body === 'object' ? body.values : null;
  const rawInterrupts = body && typeof body === 'object' ? (body as Record<string, unknown>).interrupts : null;
  const interrupts: ThreadInterrupt[] = Array.isArray(rawInterrupts)
    ? rawInterrupts.flatMap((entry) =>
        entry && typeof entry === 'object' && typeof (entry as Record<string, unknown>).id === 'string'
          ? [{ id: (entry as Record<string, unknown>).id as string, value: (entry as Record<string, unknown>).value }]
          : [],
      )
    : [];
  return { values: values && typeof values === 'object' ? values : {}, interrupts };
}

/** «Is the whole path browser → nginx → tunnel → guide alive?» */
export async function health(signal?: AbortSignal): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/health`, {
      signal: withDeadline(signal, 5000),
      cache: 'no-store',
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * What each graph runs BY DEFAULT — the first contour (the model that talks)
 * and, where the graph has one, the second (the helper / background model) —
 * keyed by graph id, e.g. `{"peterhof_agent": {"first":
 * "openai/ai-sage/GigaChat3.5-432B-A28B", "first_reasoning": "off"}}`.
 */
export type DeployModels = Record<
  string,
  { first?: string; second?: string; first_reasoning?: string; second_reasoning?: string }
>;

/**
 * The deploy's own answer to «which model is the default», read from a static
 * file the DEPLOY writes next to the page (`ci/deploy_models.py`, run by
 * `deploy-dev` off the same `.env` the service starts with, and carried into
 * every web release by `deploy-web-dev`). It is not an API call — the API
 * cannot answer this: an assistant record carries `context: {}`, and the
 * effective default is a dataclass field plus an env overlay that only the
 * graph's own `Context` resolves. Before this file existed the settings menu
 * had no checkmark until the thread's first `run_meta`, i.e. on every fresh
 * conversation. Absent (the public build ships none), unreachable or malformed
 * all read as «unknown» — the menu then falls back to `run_meta` exactly as
 * before.
 */
export async function deployModels(signal?: AbortSignal): Promise<DeployModels> {
  try {
    const response = await fetch('/deploy-models.json', {
      signal: withDeadline(signal, 3000),
      cache: 'no-store',
    });
    if (!response.ok) return {};
    const body: unknown = await response.json();
    if (!body || typeof body !== 'object' || Array.isArray(body)) return {};
    const out: DeployModels = {};
    for (const [graph, spec] of Object.entries(body as Record<string, unknown>)) {
      if (!spec || typeof spec !== 'object') continue;
      const entry: DeployModels[string] = {};
      for (const key of ['first', 'second', 'first_reasoning', 'second_reasoning'] as const) {
        const value = (spec as Record<string, unknown>)[key];
        if (typeof value === 'string' && value) entry[key] = value;
      }
      out[graph] = entry;
    }
    return out;
  } catch {
    return {};
  }
}
